import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Drawer, InputNumber, Popover, Tooltip, message } from 'antd';
import {
    AlignLeftOutlined,
    AudioOutlined,
    FontSizeOutlined,
    PlayCircleOutlined,
    SettingOutlined,
    ThunderboltOutlined,
    TranslationOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import practiceClient from '../../client/PracticeClient';
import { setPractice } from '../../redux/actions/practiceActions';
import useIsMobile from '../../hooks/useIsMobile';
import useTranslation from '../../hooks/useTranslation';
import { activateOnKey } from '../../utils/a11y';
import { formatDaysAgo } from '../../utils/relativeDays';
import { DEFAULT_BOX_COUNT, boxIndexes, boxShade, countsByBox } from './BoxDistributionBar';
import './PracticeFormatRow.css';

const ICON_BY_FORMAT = {
    SELF_ASSESSMENT: PlayCircleOutlined,
    SPELLING_FROM_AUDIO: AudioOutlined,
    SENTENCE_BUILDING_FROM_WORD: AlignLeftOutlined,
    ORIGINAL_TO_TRANSLATION: TranslationOutlined,
    TRANSLATION_TO_ORIGINAL: FontSizeOutlined,
};

/**
 * Строка одного формата практики пяти боксов.
 *
 * Цифры приходят снаружи, из сводки по колоде; init — только по кнопке. Кнопка у всех
 * строк одинаковая, обычная: заполненная teal-кнопка привлекает внимание, а когда таких
 * пять подряд, привлекать нечему. `practice` — строка сводки по формату или null, если
 * формат ещё не запускали. `boxCount` — глубина методики из той же сводки, одна на все
 * строки страницы.
 *
 * `boxSize` — распределение по коробкам, и оно приходит по колоде, а не по формату:
 * коробка лежит на слове, и своей картины у формата нет. Отсюда же и его обязательность
 * там, где показываются числа: сброс коробок — операция разрушающая и на всю колоду, и
 * выбирать в ней вслепую человек не должен. Пусто — значит чисел нет вовсе, и подписи
 * идут без них, а не с нулями.
 */
const PracticeFormatRow = ({
    title, description, practiceRequest, itemCount, practice, isLoading, isLatest, onStarted,
    onReset, boxCount = DEFAULT_BOX_COUNT, reviewIntervalDays = null, boxSize = null,
}) => {
    const { t, currentLanguage } = useTranslation();
    const isMobile = useIsMobile();
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const dispatch = useDispatch();
    const [isStarting, setIsStarting] = useState(false);
    const [isResetOpen, setIsResetOpen] = useState(false);
    const [boxesToClear, setBoxesToClear] = useState([]);
    const [isResetting, setIsResetting] = useState(false);
    // Порог закрепления внутри занятия. Приходит из сводки: показываем записанное, а не
    // умолчание. Единица — прежнее поведение, ответил верно и элемент ушёл.
    const [withinSession, setWithinSession] = useState(practice?.withinSessionCorrect ?? 1);
    // Сводка приходит после первого рендера, а начальное значение состояния берётся
    // только на нём: без этой синхронизации панель у практики с порогом три показывала
    // бы единицу — и «применить» тихо вернуло бы порог к ней.
    const storedWithinSession = practice?.withinSessionCorrect;
    useEffect(() => {
        setWithinSession(storedWithinSession ?? 1);
    }, [storedWithinSession]);

    const Icon = ICON_BY_FORMAT[practiceRequest.practiceFormat] || PlayCircleOutlined;
    const counts = boxSize ? countsByBox(boxSize, boxCount) : null;
    const practisedAgo = practice ? formatDaysAgo(practice.updatedAt, currentLanguage) : null;

    const handleStartPractice = async (sessionMode) => {
        setIsStarting(true);
        try {
            const response = await practiceClient.initializePractice(
                sessionMode ? { ...practiceRequest, sessionMode } : practiceRequest);
            dispatch(setPractice(
                response.practiceId,
                practiceRequest.practiceType,
                practiceRequest.practiceFormat,
                practiceRequest.learningMethod
            ));
            onStarted();
        } catch (error) {
            console.error('Error initializing practice:', error);
            message.error(t('practice.initFailed'));
            setIsStarting(false);
        }
    };

    // Всё, что считает срок, есть только у методик со сроком. У критерия освоения нет
    // ни боксов, чтобы их сбрасывать, ни извлекаемости, чтобы набирать пул по риску, —
    // и запрос с таким режимом его стратегия просто не читает. Карточки и вопросы
    // прячут молнию по тому же правилу; словарь показывал её при любой методике, и
    // нажатие открывало обычное занятие, ничего об этом не сказав.
    const scheduled = practiceRequest.learningMethod !== 'MASTERY_RULE';
    const canReset = scheduled && Boolean(practice);
    const resetHint = scheduled ? t('practice.reset.notStarted') : t('practice.reset.noBoxes');

    /**
     * Сброс боксов. Идёт тем же init с `boxesToClear`, что и всегда: отдельной ручки у
     * него нет, а практика у этого формата уже существует — иначе кнопка недоступна, и
     * страница выбора по-прежнему не создаёт практик сама.
     */
    const handleApplySetup = async () => {
        setIsResetting(true);
        try {
            await practiceClient.initializePractice({
                ...practiceRequest,
                ...(boxesToClear.length > 0 ? { boxesToClear } : {}),
                withinSessionCorrect: withinSession,
            });
            setIsResetOpen(false);
            const cleared = boxesToClear.length > 0;
            setBoxesToClear([]);
            message.success(cleared ? t('practice.reset.done') : t('practice.setup.applied'));
            onReset?.();
        } catch (error) {
            console.error('Не удалось применить настройки практики:', error);
            message.error(t('practice.setup.failed'));
        } finally {
            setIsResetting(false);
        }
    };

    const resetPanel = (
        <div className="practiceRow__reset">
            {/* Закрепление внутри занятия: элемент возвращается, пока не ответите верно
                нужное число раз подряд. Разнос по дням делает расписание — вместе это и
                есть successive relearning, которое до сих пор нельзя было включить
                ниоткуда: поле принимал только запрос. */}
            <label className="practiceRow__setupRow">
                <span className="practiceRow__resetHint">{t('practice.setup.withinSession')}</span>
                <InputNumber
                    min={1}
                    max={10}
                    size="small"
                    value={withinSession}
                    onChange={(value) => setWithinSession(value || 1)}
                />
            </label>
            <p className="practiceRow__resetHint">{t('practice.setup.withinSessionHint')}</p>

            <p className="practiceRow__resetHint practiceRow__setupSection">{t('practice.reset.hint')}</p>
            <p className="practiceRow__resetHint">{t('practice.reset.deckWide')}</p>
            <Checkbox.Group
                className="practiceRow__resetBoxes"
                value={boxesToClear}
                onChange={setBoxesToClear}
                options={boxIndexes(boxCount).map((box) => ({
                    value: box,
                    label: counts
                        ? `${t('practice.box', { number: box + 1 })} · ${counts[box]}`
                        : t('practice.box', { number: box + 1 }),
                }))}
            />
            <div className="practiceRow__resetActions">
                <Button size="small" onClick={() => setIsResetOpen(false)}>
                    {t('practice.reset.cancel')}
                </Button>
                <Button
                    size="small"
                    type="primary"
                    loading={isResetting}
                    onClick={handleApplySetup}
                >
                    {boxesToClear.length > 0 ? t('practice.reset.apply') : t('practice.setup.apply')}
                </Button>
            </div>
        </div>
    );

    // На телефоне в строку помещается заголовок, кнопка и две иконки — цифры по
    // коробкам туда не влезают. Они уходят в нижнюю панель по тапу на строку: тап
    // именно на детали, а не на запуск, потому что случайное касание не должно
    // открывать практику.
    const daysByBox = reviewIntervalDays ? [0, ...reviewIntervalDays] : null;
    // Подпись срока коробки: пусто, если интервалов нет, «сейчас» для нулевой, дни дальше.
    const whenLabel = (days) => {
        if (days === undefined || days === null) {
            return '';
        }
        return days === 0 ? t('practice.reviewNow') : t('practice.reviewInDays', { count: days });
    };
    const details = (
        <div className="practiceRow__details">
            <p className="practiceRow__detailsDesc">{description}</p>
            {counts ? (
                <ul className="practiceRow__boxes">
                    {boxIndexes(boxCount).map((box) => (
                        <li className="practiceRow__box" key={box}>
                            <span
                                className="practiceRow__boxSwatch"
                                style={{ background: boxShade(box, boxCount) }}
                            />
                            <span className="practiceRow__boxName">
                                {t('practice.box', { number: box + 1 })}
                            </span>
                            <span className="practiceRow__boxWhen">{whenLabel(daysByBox?.[box])}</span>
                            <b className="practiceRow__boxCount">{counts[box]}</b>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="practiceRow__detailsDesc">
                    {t('practice.allWordsInFirstBox', { count: itemCount })}
                </p>
            )}
            <Button
                block
                type="primary"
                loading={isStarting}
                disabled={isLoading}
                onClick={() => {
                    setIsDetailsOpen(false);
                    handleStartPractice();
                }}
            >
                {t('practice.start')}
            </Button>
            {canReset && (
                <div className="practiceRow__detailsReset">{resetPanel}</div>
            )}
        </div>
    );

    // Колонки под полосу у строки нет: картина одна на колоду и живёт в панели методики.
    // Пустая колонка в триста пикселей на широком экране разводила заголовок и кнопку по
    // разным краям, а между ними держала пустоту.
    // На узком экране строка целиком открывает детали, и тогда она — кнопка и для
    // клавиатуры: роль, фокус и Enter/Space. На широком экране деталей по строке нет,
    // и роли у неё тоже нет — иначе читалка обещала бы действие, которого не будет.
    const openDetails = (event) => {
        // Клик по кнопке — это кнопка, а не открытие деталей.
        if (!event.target.closest('button')) {
            setIsDetailsOpen(true);
        }
    };
    const rowInteraction = isMobile
        ? { role: 'button', tabIndex: 0, onClick: openDetails, onKeyDown: activateOnKey(() => setIsDetailsOpen(true)) }
        : {};

    return (
        <div className="practiceRow practiceRow--noBar" {...rowInteraction}>
            <div className="practiceRow__icon"><Icon /></div>

            <div className="practiceRow__text">
                <div className="practiceRow__title">
                    {title}
                    {isLatest && <span className="practiceRow__badge">{t('practice.lastPractised')}</span>}
                </div>
                <div className="practiceRow__sub">
                    <span className="practiceRow__desc">{description}</span>
                    {practisedAgo && (
                        <>
                            {/* Точка перед «когда» исчезает вместе с описанием: на узком
                                экране описания нет, и разделитель начинал бы строку. */}
                            <span className="practiceRow__dot practiceRow__desc">·</span>
                            <span>{practisedAgo}</span>
                        </>
                    )}
                </div>
            </div>

            {/* Полосы здесь нет: след памяти один на слово, значит и картина одна на
                колоду — она живёт в панели методики над списком форматов. */}

            <Button
                className="practiceRow__button"
                onClick={() => handleStartPractice()}
                loading={isStarting}
                disabled={isLoading}
            >
                {t('practice.start')}
            </Button>

            {scheduled && (
                <Tooltip title={t('practice.riskReview')}>
                    <button
                        type="button"
                        className="practiceRow__risk"
                        aria-label={t('practice.riskReview')}
                        disabled={isLoading || isStarting}
                        onClick={() => handleStartPractice('RISK_REVIEW')}
                    >
                        <ThunderboltOutlined />
                    </button>
                </Tooltip>
            )}

            <Popover
                open={isResetOpen && canReset}
                onOpenChange={(open) => setIsResetOpen(open && canReset)}
                trigger="click"
                placement="bottomRight"
                title={t('practice.setup.title')}
                content={resetPanel}
            >
                <Tooltip title={canReset ? t('practice.setup.title') : resetHint}>
                    {/* Обёртка нужна для подсказки над выключенной кнопкой: у disabled нет
                        событий мыши, и без неё причина, по которой сброс недоступен,
                        осталась бы невысказанной. */}
                    <span className="practiceRow__gearWrap">
                        <button
                            type="button"
                            className="practiceRow__gear"
                            aria-label={t('practice.setup.title')}
                            disabled={!canReset || isLoading}
                        >
                            <SettingOutlined />
                        </button>
                    </span>
                </Tooltip>
            </Popover>

            {isMobile && (
                <Drawer
                    open={isDetailsOpen}
                    onClose={() => setIsDetailsOpen(false)}
                    placement="bottom"
                    height="auto"
                    title={title}
                >
                    {details}
                </Drawer>
            )}
        </div>
    );
};

export default PracticeFormatRow;
